'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import LargePropertyCard from '@components/LargePropertyCard'
import dynamic from 'next/dynamic'
const SearchMap = dynamic(() => import('@components/SearchMap'), { ssr: false })
import MobileSearchMap from '@components/MobileSearchMap'
import MobileMapPropertyCard from '@components/MobileMapPropertyCard'
import { getPanel } from '@services/getPanel'
import { getPropertyCardInfo } from '@services/getPropertyCardInfo'
import LoadingCard from '@components/LoadingCard'
import { saleTypes } from '@constants/saleTypes'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'

// scrollMode: 'infinite' | 'loadmore' | 'pagination'
const SearchPanelCards = ({ params, searchParams, cardStyle, mapCardStyle, dictionary, lang = 'en', scrollMode = 'pagination', searchPageConfig }) => {

    // ── Resolve config ─────────────────────────────────────────────────────────
    const cfg                = searchPageConfig ?? {}
    const resolvedScrollMode = cfg.scrollMode   ?? scrollMode

    // List view grid
    const colsDesktop  = cfg.colsDesktop ?? 4
    const colsTablet   = cfg.colsTablet  ?? 2
    const colsMobile   = cfg.colsMobile  ?? 1
    const cardGap      = cfg.cardGap     ?? 12

    // Button / pagination styles
    const btnBg        = cfg.btnBg               ?? 'var(--thirdcolor)'
    const btnTextColor = cfg.btnTextColor         ?? '#ffffff'
    const btnRadius    = cfg.btnRadius            ?? 6
    const pActiveBg    = cfg.paginationActiveBg   ?? 'var(--thirdcolor)'
    const pActiveTc    = cfg.paginationActiveText ?? '#ffffff'
    const pBorderColor = cfg.paginationBorderColor ?? '#e5e7eb'

    // Map layout
    const mapCfg         = cfg.map ?? {}
    const splitRatio     = mapCfg.splitRatio     ?? 40
    const sidebarSide    = mapCfg.sidebarSide    ?? 'left'
    const mapColsDesktop = mapCfg.mapColsDesktop ?? 1

    // ── State ──────────────────────────────────────────────────────────────────
    let userEmail = null

    const router    = useRouter()
    const pathname  = usePathname()
    const urlParams = useSearchParams()

    const [isLoading, setIsLoading]                 = useState(false)
    const [page, setPage]                            = useState(resolvedScrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1)
    const [allPages, setAllPages]                    = useState(1)
    const [properties, setProperties]               = useState([])
    const [propertyCount, setPropertyCount]          = useState(0)
    const [showMap, setShowMap]                      = useState(false)
    const [coordinates, setCoordinates]              = useState([])
    const [hoveredProperty, setHoveredProperty]      = useState(null)
    const [activeProperty, setActiveProperty]        = useState({})
    const [activePropertyLat, setActivePropertyLat] = useState(13.732772)
    const [activePropertyLng, setActivePropertyLng] = useState(100.5801953)

    const swiperRef      = useRef(null)
    const propertiesRef   = useRef([])

    const handleMouseEnter = (propertyId) => setHoveredProperty(propertyId)
    const handleMouseLeave = () => setHoveredProperty(null)

    const handleSlideChange = (swiper) => {
        const idx = swiper.activeIndex
        const slide = swiper.slides[idx]
        const lat = Number(slide.dataset.latitude)
        const lng = Number(slide.dataset.longitude)
        setActiveProperty(slide.id)
        if (isFinite(lat) && lat !== 0) setActivePropertyLat(lat)
        if (isFinite(lng) && lng !== 0) setActivePropertyLng(lng)
    }

    const handleMarkerClick = async ({ _id, latitude, longitude }) => {
        setActiveProperty(_id)
        setActivePropertyLat(Number(latitude))
        setActivePropertyLng(Number(longitude))

        // Check if property is already in the loaded list
        const idx = propertiesRef.current.findIndex(p => p.id === _id || p._id === _id)
        if (idx !== -1) {
            swiperRef.current?.slideTo(idx)
            return
        }

        // Not in list — fetch from server and prepend to swiper
        try {
            const fetched = await getPropertyCardInfo(_id)
            if (!fetched || fetched.message) return
            fetched.latitude  = latitude
            fetched.longitude = longitude
            const next = [fetched, ...propertiesRef.current]
            propertiesRef.current = next
            setProperties(next)
            // Wait for swiper to re-render with new slide then jump to it
            setTimeout(() => swiperRef.current?.slideTo(0), 50)
        } catch(e) {
            console.error('getPropertyCardInfo failed', e)
        }
    }

    // ── Data fetching ──────────────────────────────────────────────────────────
    const getData = async (pageNumber) => {
        setIsLoading(true)
        const result = await getPanel({
            slug: params.slug,
            // saleType:    searchParams.saleType    || saleTypes[lang][0].value,
            // bts:         searchParams.bts         || '',
            // name:        searchParams.name        || '',
            // district:    searchParams.district    || '',
            // districts:   searchParams.districts   || '',
            // area:        searchParams.area        || '',
            // location:    searchParams.location    || '',
            // project:     searchParams.project     || '',
            // category:    searchParams.category    || '',
            // categories:  searchParams.categories  || '',
            // minBedrooms: searchParams.minBedrooms || '',
            // maxBedrooms: searchParams.maxBedrooms || '',
            // room:        searchParams.room        || '',
            // currency:    searchParams.currency    || 'thb',
            // minprice:    searchParams.minprice    || '',
            // maxprice:    searchParams.maxprice    || '',
            // petfriendly: searchParams.petfriendly || '',
            // seaview:     searchParams.seaview     || '',
            // beachfront:  searchParams.beachfront  || '',
            // map:         searchParams.map         || false,
            // sort:        searchParams.sort        || 'datedesc',
            page:        pageNumber,
        }, userEmail, lang)

        if (pageNumber > 1 && resolvedScrollMode !== 'pagination') {
            const next = [...propertiesRef.current, ...result.properties]
            propertiesRef.current = next
            setProperties(next)
        } else {
            propertiesRef.current = result.properties
            setProperties(result.properties)
        }
        setAllPages(result.pages)
        setPropertyCount(result.count)
        setCoordinates(result.coordinates)
        if (result.properties.length > 0 && pageNumber === 1) {
            setActiveProperty(result.properties[0].id)
            setActivePropertyLat(Number(result.properties[0].latitude))
            setActivePropertyLng(Number(result.properties[0].longitude))
        }
        setIsLoading(false)
    }

    useEffect(() => {
        const urlPage = resolvedScrollMode === 'pagination' ? (Number(urlParams.get('page')) || 1) : 1
        setPage(urlPage)
        setProperties([])
        setShowMap(searchParams?.map)
        getData(urlPage)
    }, [searchParams])

    useEffect(() => {
        getData(page)
    }, [page])

    // ── Pagination ─────────────────────────────────────────────────────────────
    const goToPage = (p) => {
        const next = Math.max(1, Math.min(allPages, p))
        setPage(next)
        const params = new URLSearchParams(urlParams.toString())
        if (next === 1) params.delete('page')
        else params.set('page', String(next))
        router.push(`${pathname}?${params.toString()}`, { scroll: true })
    }

    const PaginationControls = () => {
        if (resolvedScrollMode !== 'pagination' || allPages <= 1) return null
        const getPageNumbers = () => {
            if (allPages <= 7) return Array.from({ length: allPages }, (_, i) => i + 1)
            if (page <= 4)             return [1, 2, 3, 4, 5, '...', allPages]
            if (page >= allPages - 3)  return [1, '...', allPages - 4, allPages - 3, allPages - 2, allPages - 1, allPages]
            return [1, '...', page - 1, page, page + 1, '...', allPages]
        }
        const baseBtn = { padding: '6px 10px', borderRadius: btnRadius, background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', border: `1px solid ${pBorderColor}` }
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, paddingTop: 32, paddingBottom: 32 }}>
                <button disabled={page === 1} onClick={() => goToPage(page - 1)}
                    style={{ ...baseBtn, opacity: page === 1 ? 0.3 : 1 }}>‹</button>
                {getPageNumbers().map((p, i) =>
                    p === '...'
                        ? <span key={`e-${i}`} style={{ padding: '6px 4px', fontSize: 13, color: '#9ca3af' }}>…</span>
                        : <button key={p} onClick={() => goToPage(p)} style={{
                            ...baseBtn,
                            border:      `1px solid ${p === page ? pActiveBg : pBorderColor}`,
                            background:  p === page ? pActiveBg : '#fff',
                            color:       p === page ? pActiveTc : '#374151',
                            fontWeight:  p === page ? 700 : 400,
                          }}>{p}</button>
                )}
                <button disabled={page === allPages} onClick={() => goToPage(page + 1)}
                    style={{ ...baseBtn, opacity: page === allPages ? 0.3 : 1 }}>›</button>
            </div>
        )
    }

    // ── Load More ──────────────────────────────────────────────────────────────
    const LoadMoreButton = () => {
        if (resolvedScrollMode !== 'loadmore' || isLoading || page >= allPages) return null
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 32, paddingBottom: 32 }}>
                <button onClick={() => setPage(p => p + 1)}
                    style={{ width: 300, padding: '8px 16px', background: btnBg, color: btnTextColor, borderRadius: btnRadius, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    {dictionary?.property?.loadMore ?? 'Load More'}
                </button>
            </div>
        )
    }

    // ── Infinite scroll trigger ────────────────────────────────────────────────
    const newLimit = () => {
        if (resolvedScrollMode === 'infinite' && page < allPages) setPage(p => p + 1)
    }

    // ── Loading placeholders ───────────────────────────────────────────────────
    const LoadingCards = () => (
        <>
            {isLoading && <div className='m-2'><LoadingCard /></div>}
            {isLoading && <div className='m-2'><LoadingCard /></div>}
            {isLoading && <div className='m-2'><LoadingCard /></div>}
            {isLoading && <div className='m-2'><LoadingCard /></div>}
            {isLoading && <div className='m-2'><LoadingCard /></div>}
        </>
    )

    // ── Map sidebar/panel styles driven by config ──────────────────────────────
    const sidebarStyle = {
        flexBasis:   `${splitRatio}%`,
        flexShrink:  0,
        order:       sidebarSide === 'right' ? 2 : 0,
        borderRight: sidebarSide === 'left'  ? '1px solid #e5e7eb' : 'none',
        borderLeft:  sidebarSide === 'right' ? '1px solid #e5e7eb' : 'none',
    }
    const mapPanelStyle = {
        flex:  1,
        order: sidebarSide === 'right' ? 0 : 2,
    }

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className='container-wide sm:min-h-[80vh] gap-4 flex md:flex-row flex-col flex-nowrap md:flex-wrap mx-auto lg:px-3 sm:px-0 px-3'>
            {showMap ? (
                <div className='w-full relative flex flex-row mx-auto'>

                    {/* ── Desktop sidebar ── */}
                    <div
                        className='md:flex hidden md:h-[80vh] h-full p-3 overflow-hidden overflow-y-scroll flex-col items-center scrollable'
                        style={sidebarStyle}
                    >
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${mapColsDesktop}, 1fr)`,
                            gap: 8,
                            width: '100%',
                        }}>
                            {properties.map((property, index) => (
                                <div
                                    key={property.id}
                                    id={property.id}
                                    onMouseEnter={() => handleMouseEnter(property.id)}
                                    onMouseLeave={handleMouseLeave}
                                    className='mb-2'
                                >
                                    <LargePropertyCard
                                        lang={lang}
                                        dictionary={dictionary.card}
                                        property={property}
                                        isLast={index === properties.length - 1}
                                        newLimit={newLimit}
                                        cardStyle={cardStyle}
                                    />
                                </div>
                            ))}
                        </div>
                        <LoadingCards />
                        <LoadMoreButton />
                        <PaginationControls />
                    </div>

                    {/* ── Mobile swiper — wrapper pointer-events:none so map markers stay tappable ── */}
                    <div className='md:hidden block absolute bottom-0 left-0 right-0 z-[52]' style={{ pointerEvents: 'none' }}>
                        <div className='sm:hidden block' style={{ pointerEvents: 'auto' }}>
                            <Swiper slidesPerView={1} onSwiper={s => { swiperRef.current = s }} onSlideChange={handleSlideChange}>
                                {properties.map((property, index) => (
                                    <SwiperSlide
                                        key={property.id}
                                        id={property.id}
                                        data-latitude={Number(property.latitude)}
                                        data-longitude={Number(property.longitude)}
                                    >
                                        <MobileMapPropertyCard
                                            lang={lang}
                                            dictionary={dictionary.card}
                                            property={property}
                                            cardStyle={mapCardStyle}
                                            isLast={index === properties.length - 1}
                                            newLimit={newLimit}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>

                    {/* ── Desktop map ── */}
                    <div
                        className='md:block hidden md:h-[80vh] h-full md:relative absolute overflow-hidden'
                        style={mapPanelStyle}
                    >
                        <SearchMap
                            cardStyle={mapCardStyle}
                            coordinates={coordinates}
                            hoveredProperty={hoveredProperty}
                            latitude={searchParams.latitude}
                            longitude={searchParams.longitude}
                            lang={lang}
                            dictionary={dictionary}
                            mapConfig={mapCfg}
                        />
                    </div>

                    {/* ── Mobile map ── */}
                    <div className='md:hidden block w-full h-full z-[51]'>
                        <MobileSearchMap
                            coordinates={coordinates}
                            hoveredProperty={activeProperty}
                            zoomControl={false}
                            latitude={activePropertyLat}
                            longitude={activePropertyLng}
                            lang={lang}
                            dictionary={dictionary}
                            mapConfig={mapCfg}
                            cardStyle={mapCardStyle}
                            onMarkerClick={handleMarkerClick}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <style>{`
                        #prop-grid {
                            display: grid;
                            gap: ${cardGap}px;
                            grid-template-columns: repeat(${colsMobile}, 1fr);
                        }
                        @media (min-width: 640px) {
                            #prop-grid { grid-template-columns: repeat(${colsTablet}, 1fr); }
                        }
                        @media (min-width: 1024px) {
                            #prop-grid { grid-template-columns: repeat(${colsDesktop}, 1fr); }
                        }
                    `}</style>
                    <div id="prop-grid">
                        {properties.map((property, index) => (
                            <LargePropertyCard
                                key={property.id}
                                lang={lang}
                                saleType={searchParams.saleType}
                                dictionary={dictionary.card}
                                property={property}
                                isLast={index === properties.length - 1}
                                newLimit={newLimit}
                                cardStyle={cardStyle}
                            />
                        ))}
                        <LoadingCards />
                    </div>
                    <LoadMoreButton />
                    <PaginationControls />
                </>
            )}
        </div>
    )
}

export default SearchPanelCards